import { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from "react";
import posthog, { captureError } from "@/lib/posthog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DateFilterKey, DATE_FILTER_OPTIONS, resolveDateFilter } from "@/utils/dateFilters";
import { DateFilter } from "@/components/ui/DateFilter";
import {
  AlertTriangle,
  Info,
  XCircle,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Minus,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Activity,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Download,
  Calendar,
  ExternalLink,
  Landmark,
} from "lucide-react";
import { Account, CashflowKPIs, BusinessOverviewKPIs, Invoice, ReceivablesKPIs, AgedReceivablesKPIs, ExpensePayableItem } from "@blinkbooks/types";
import apiClient from "@/utils/httpClient";
import { fetchCashflowKPIs, fetchBusinessKPIs, fetchReceivablesKPIs, fetchAgedReceivablesKPIs, fetchCashInflowDetails, CashInflowRow, fetchCashOutflowDetails, CashOutflowRow } from "@/services/kpi.service";

interface BankAccountRow {
  id: string;
  name: string;
  type: string;
  bookBalance: number | null;
  currency: string;
  last4Digits?: string;
}
import { formatAmount, formatAmountCompact } from "@/utils/expenseFormatters";
import { formatDistanceToNow, differenceInDays, parseISO, format, isValid } from "date-fns";
import { getSyncStatus } from "@/services/sync.quickbooks.service";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toPng } from "html-to-image";

// ── Types ──────────────────────────────────────────────────────────────────────


type SignalLevel = "critical" | "high" | "warning" | "info";

interface FinancialSignal {
  id: string;
  level: SignalLevel;
  category: string;
  title: string;
  description: string;
  action: string;
  value?: number;
}

interface SignalsResult {
  signals: FinancialSignal[];
  generatedAt: string;
}

type KpiDetailKey =
  | "revenue" | "collected" | "avg-days-collect" | "expenses"
  | "at-risk" | "stuck-30" | "current" | "receivables" | "open-ar"
  | "cash-on-hand" | "cash-net-profit" | "burn-rate" | "runway" | "net-cash-flow";

interface CollectedPaymentLine {
  date: string;
  method: string;
  amount: number;
}

interface CollectedInvoiceRow {
  invoiceId: string;
  invoiceNumber: string;
  invoiceStatus: string;
  invoiceTotalAmount: number;
  invoiceBalance: number;
  customerName: string;
  currency: string;
  issueDate: string;
  paidInPeriod: number;
  payments: CollectedPaymentLine[];
}

// ── Utility ────────────────────────────────────────────────────────────────────

function fallbackRange(): { startDate: string; endDate: string } {
  const to = new Date();
  const from = new Date(to);
  from.setDate(to.getDate() - 30);
  const fmt = (d: Date) => d.toISOString().split("T")[0]!;
  return { startDate: fmt(from), endDate: fmt(to) };
}

// ── SmartViewCard ──────────────────────────────────────────────────────────────

const CARD_COMPACT_THRESHOLD = 185;

const SmartViewCard = ({
  label, value, compactValue, countLabel, subtitle, colorScheme, onClick, loading = false, tag, compact = false, badge,
}: {
  label: string; value: string; compactValue?: string; countLabel?: string; subtitle?: string;
  colorScheme: "red" | "amber" | "emerald" | "sky" | "purple";
  onClick?: () => void; loading?: boolean;
  tag?: { type: "live" | "period"; text: string };
  compact?: boolean;
  badge?: string;
}) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const [isNarrow, setIsNarrow] = useState(false);
  useLayoutEffect(() => {
    const el = btnRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([e]) => setIsNarrow(e.contentRect.width < CARD_COMPACT_THRESHOLD));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const scheme = {
    red: { bg: "bg-red-50    hover:bg-red-100", border: "border-red-200", value: "text-red-700", label: "text-red-600" },
    amber: { bg: "bg-amber-50  hover:bg-amber-100", border: "border-amber-200", value: "text-amber-700", label: "text-amber-600" },
    emerald: { bg: "bg-emerald-50 hover:bg-emerald-100", border: "border-emerald-200", value: "text-emerald-700", label: "text-emerald-600" },
    sky: { bg: "bg-sky-50    hover:bg-sky-100", border: "border-sky-200", value: "text-sky-700", label: "text-sky-600" },
    purple: { bg: "bg-purple-50 hover:bg-purple-100", border: "border-purple-200", value: "text-purple-700", label: "text-purple-600" },
  }[colorScheme];

  const displayValue = isNarrow && compactValue ? compactValue : value;

  return (
    <button ref={btnRef} onClick={onClick} disabled={!onClick || loading}
      className={`w-full rounded-xl border ${scheme.bg} ${scheme.border} ${compact ? "p-2.5" : "p-4"} text-left shadow-sm flex flex-col gap-0.5 transition-colors group ${onClick ? "cursor-pointer" : "cursor-default"}`}
    >
      {/* Line 1: label + badge + arrow */}
      <div className="w-full flex items-center justify-between gap-1">
        <div className="flex items-center gap-1 min-w-0">
          <p className={`${compact ? "text-[10px]" : "text-xs"} font-semibold uppercase tracking-wide leading-tight ${scheme.label} truncate`}>{label}</p>
          {badge && <span className={`text-[11px] font-medium ${scheme.label} opacity-80 border ${scheme.border} px-1.5 py-px rounded leading-none whitespace-nowrap shrink-0`}>{badge}</span>}
        </div>
        {onClick && <ArrowRight className={`h-3 w-3 ${scheme.label} opacity-0 group-hover:opacity-100 transition-opacity shrink-0`} />}
      </div>
      {/* Line 2: time tag */}
      {tag && (
        tag.type === "live" ? (
          <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap flex items-center gap-0.5 self-start">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block animate-pulse" />
            Live{tag.text && <><span className="opacity-50 mx-0.5">·</span>{tag.text}</>}
          </span>
        ) : (
          <span className="text-[10px] font-medium text-primary/90 bg-primary/10 px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap self-start">
            {tag.text}
          </span>
        )
      )}
      {/* Line 3: value */}
      <p className={`${compact ? "text-lg" : "text-2xl"} font-bold tabular-nums leading-tight ${loading ? "text-muted-foreground" : scheme.value}`}>
        {loading ? "—" : displayValue}
      </p>
      {countLabel && (
        <p className={`${compact ? "text-xs" : "text-sm"} font-medium tabular-nums ${loading ? "text-muted-foreground" : scheme.value} opacity-70`}>
          {loading ? "—" : countLabel}
        </p>
      )}
      {!compact && subtitle && <p className={`text-xs ${scheme.label} opacity-70`}>{subtitle}</p>}
    </button>
  );
};

// ── MetricCard ─────────────────────────────────────────────────────────────────

const MetricCard = ({
  label, value, compactValue, subtitle, formula, valueClass = "text-foreground",
  trend, periodTag, liveTag, isLoading, onClick, bankOnly,
}: {
  label: string; value: string; compactValue?: string; subtitle?: string; formula?: string;
  valueClass?: string; trend?: "up" | "down" | "flat"; periodTag?: string; liveTag?: boolean;
  isLoading?: boolean; onClick?: () => void; bankOnly?: boolean;
}) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const [isNarrow, setIsNarrow] = useState(false);
  useLayoutEffect(() => {
    const el = btnRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([e]) => setIsNarrow(e.contentRect.width < CARD_COMPACT_THRESHOLD));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const displayValue = isNarrow && compactValue ? compactValue : value;

  return (
    <button ref={btnRef} onClick={onClick} disabled={!onClick}
      className={`rounded-xl border bg-card p-4 shadow-sm flex flex-col gap-1 text-left w-full overflow-hidden ${onClick ? "cursor-pointer hover:bg-muted/30 transition-colors group" : "cursor-default"}`}
    >
      {/* Line 1: label (clamped to 2 lines) + tooltip + arrow */}
      <div className="w-full flex items-start justify-between gap-1 min-h-[2rem]">
        <div className="flex items-start gap-1 min-w-0">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide leading-tight line-clamp-2">{label}</p>
          {formula && !isLoading && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 text-muted-foreground/50 hover:text-muted-foreground cursor-default shrink-0 mt-px" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs whitespace-pre-line">{formula}</TooltipContent>
            </Tooltip>
          )}
        </div>
        {onClick && (
          <ArrowRight className="h-3 w-3 text-muted-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-px" />
        )}
      </div>
      {/* Line 2: period/live tag — always reserves space so values align across cards */}
      <div className="min-h-[1.25rem] flex items-center">
        {liveTag ? (
          <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap flex items-center gap-0.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" />Live
          </span>
        ) : periodTag ? (
          <span className="text-[10px] font-medium text-primary/90 bg-primary/10 px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap">
            {periodTag}
          </span>
        ) : null}
      </div>
      {/* Line 3: value + trend */}
      <div className="flex items-end gap-1.5 min-w-0">
        <p className={`text-2xl font-bold tabular-nums leading-tight truncate min-w-0 ${displayValue === "—" || displayValue === "No data" ? "text-muted-foreground" : valueClass}`}>
          {displayValue}
        </p>
        {trend === "up" && <TrendingUp className="h-4 w-4 text-emerald-500 mb-0.5 shrink-0" />}
        {trend === "down" && <TrendingDown className="h-4 w-4 text-red-500    mb-0.5 shrink-0" />}
        {trend === "flat" && <Minus className="h-4 w-4 text-muted-foreground mb-0.5 shrink-0" />}
      </div>
      {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
    </button>
  );
};

// ── SignalCard ─────────────────────────────────────────────────────────────────

const SIGNAL_CONFIG: Record<SignalLevel, {
  icon: React.ComponentType<{ className?: string }>;
  iconCls: string; titleCls: string; bg: string; border: string;
}> = {
  critical: { icon: XCircle, iconCls: "text-red-600", titleCls: "text-red-700", bg: "bg-red-50", border: "border-red-200" },
  high: { icon: AlertTriangle, iconCls: "text-red-500", titleCls: "text-red-600", bg: "bg-red-50", border: "border-red-200" },
  warning: { icon: AlertTriangle, iconCls: "text-amber-600", titleCls: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
  info: { icon: Info, iconCls: "text-sky-600", titleCls: "text-sky-700", bg: "bg-sky-50", border: "border-sky-200" },
};

const SignalCard = ({ signal }: { signal: FinancialSignal }) => {
  const [expanded, setExpanded] = useState(false);
  const cfg = SIGNAL_CONFIG[signal.level];
  const Icon = cfg.icon;
  return (
    <div className={`rounded-lg border ${cfg.bg} ${cfg.border} overflow-hidden`}>
      <button className="w-full flex items-start gap-3 p-4 text-left" onClick={() => setExpanded((v) => !v)}>
        <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${cfg.iconCls}`} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${cfg.titleCls}`}>{signal.title}</p>
        </div>
        {expanded
          ? <ChevronDown className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          : <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 pt-0 border-t border-current/10">
          <p className="text-sm text-foreground/80 pt-3">{signal.description}</p>
        </div>
      )}
    </div>
  );
};

// ── KPI Detail Sheet helpers ───────────────────────────────────────────────────

const DetailRow = ({ label, value, bold }: { label: string; value: string; bold?: boolean }) => (
  <div className="flex justify-between text-sm gap-4">
    <span className={bold ? "font-semibold" : "text-muted-foreground"}>{label}</span>
    <span className={`${bold ? "font-bold" : "font-medium"} text-right`}>{value}</span>
  </div>
);

const FormulaBox = ({ formula }: { formula: string }) => (
  <div className="rounded-lg bg-muted/30 border p-3">
    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Formula</p>
    <p className="text-sm whitespace-pre-line">{formula}</p>
  </div>
);

function getKpiFormula(key: KpiDetailKey, period: string, months?: number): string {
  const p = period;
  const mo = months ?? 1;
  const moStr = `${mo} ${mo === 1 ? "month" : "months"}`;
  const map: Partial<Record<KpiDetailKey, string>> = {
    revenue: `Operational bank inflows · excludes transfers, loans & financing · ${p}`,
    collected: `Payments received from invoices · ${p}`,
    "avg-days-collect": `Weighted average days from invoice issue to payment · Σ(amount × days) / Σ(amount) · ${p}`,
    expenses: `Operational bank outflows · vendor payments, payroll, expenses, taxes · excludes transfers & investments · ${p}`,
    "at-risk": `Unpaid or partially paid invoices · 1–30 days past due · All Time`,
    "stuck-30": `Unpaid or partially paid invoices · more than 30 days past due · All Time`,
    current: `Unpaid or partially paid invoices · not yet due · All Time`,
    "open-ar": `All outstanding AR as of end of ${p} · current + at risk + overdue · sourced from QuickBooks`,
    receivables: `All invoices issued · ${p}`,
    "cash-on-hand": `Aggregated balance of connected bank accounts · excludes credit cards · Live`,
    "cash-net-profit": `Cash Inflows − Cash Outflows · ${p}`,
    "burn-rate": `Σ(abs(negative monthly net cash flows)) ÷ ${moStr} · ${p}`,
    runway: `Cash Balance ÷ Monthly Burn Rate`,
    "net-cash-flow": `All bank inflows minus outflows · All Time · Live`,
  };
  return map[key] ?? "";
}

const KPI_DETAIL_LABELS: Partial<Record<KpiDetailKey, string>> = {
  "revenue": "Cash Inflows",
  "collected": "Collected",
  "avg-days-collect": "Average Days to Collect",
  "expenses": "Cash Outflows",
  "at-risk": "At Risk",
  "stuck-30": "Overdue",
  "current": "Current",
  "open-ar": "Account Receivables",
  "receivables": "Total Billed",
  "cash-on-hand": "Cash Balance",
  "cash-net-profit": "Operating Cash Flow",
  "burn-rate": "Monthly Burn Rate",
  "runway": "Runway",
  "net-cash-flow": "Net Cash Flow",
};

interface KpiDetailSheetProps {
  open: boolean;
  onClose: () => void;
  kpiKey: KpiDetailKey | null;
  invoices: Invoice[];
  collectedInvoices: CollectedInvoiceRow[];
  cashInflows: CashInflowRow[];
  cashOutflows: CashOutflowRow[];
  bankAccounts: BankAccountRow[];
  expenses: ExpensePayableItem[];
  loading: boolean;
  receivables: ReceivablesKPIs | null;
  agedAR: AgedReceivablesKPIs | null;
  endDate: string;
  accounts: Account[];
  period: string;
  periodMonths: number;
  dateFilterKey: string;
  onNavigate?: (tab: string, dateFilter?: string) => void;
  numerics: {
    revenueMTD: number; expensesMTD: number; netProfit: number;
    burnRate: number; netCashFlow: number; netCashFlowQuarter: number;
    runwayMonths: number; avgDays: number; avgDaysCount: number;
    cashOnHand: number;
  };
}

const KPI_NAV_TARGET: Partial<Record<KpiDetailKey, string>> = {
  "receivables": "sales-receivables",
  "collected": "sales-receivables",
  "current": "sales-receivables",
  "at-risk": "sales-receivables",
  "stuck-30": "sales-receivables",
  "open-ar": "sales-receivables",
  "revenue": "sales-receivables",
  "avg-days-collect": "sales-receivables",
  "expenses": "expenses-payables",
};

const KpiDetailSheet = ({
  open, onClose, kpiKey, invoices, collectedInvoices, cashInflows, cashOutflows, bankAccounts, expenses, loading,
  receivables, agedAR, endDate, accounts, period, periodMonths, dateFilterKey, onNavigate, numerics,
}: KpiDetailSheetProps) => {
  const detailCashInflows = cashInflows;
  const detailCashOutflows = cashOutflows;
  const label = kpiKey ? (KPI_DETAIL_LABELS[kpiKey] ?? kpiKey) : "";

  const [atRiskGrouped, setAtRiskGrouped] = useState(true);
  const [atRiskCustomer, setAtRiskCustomer] = useState<string>("all");
  const [aggSort, setAggSort] = useState<{ field: "customer" | "count" | "amount" | "days"; dir: "asc" | "desc" }>({ field: "amount", dir: "desc" });
  const [invSort, setInvSort] = useState<{ field: "customer" | "invoice" | "amount" | "days"; dir: "asc" | "desc" }>({ field: "days", dir: "desc" });
  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set());
  const [expandedVendors, setExpandedVendors] = useState<Set<string>>(new Set());

  useEffect(() => {
    setAtRiskCustomer("all");
    setAtRiskGrouped(true);
    setAggSort({ field: "amount", dir: "desc" });
    setInvSort({ field: "days", dir: "desc" });
    setExpandedCustomers(new Set());
    setExpandedVendors(new Set());
  }, [kpiKey]);

  const toggleCustomer = (name: string) =>
    setExpandedCustomers(prev => {
      const next = new Set(prev);
      if (next.has(name)) { next.delete(name); } else { next.add(name); }
      return next;
    });

  const toggleVendor = (name: string) =>
    setExpandedVendors(prev => {
      const next = new Set(prev);
      if (next.has(name)) { next.delete(name); } else { next.add(name); }
      return next;
    });

  const renderContent = () => {
    if (!kpiKey) return null;

    // ── Formula / calculation breakdown KPIs ──
    if (["cash-net-profit", "burn-rate", "runway", "net-cash-flow"].includes(kpiKey)) {
      return (
        <div className="space-y-4">
          <FormulaBox formula={getKpiFormula(kpiKey, period, periodMonths)} />
          <div className="space-y-2">
            {kpiKey === "cash-net-profit" && (
              <>
                <DetailRow label="Cash Inflows" value={formatAmount(numerics.revenueMTD)} />
                <DetailRow label="Cash Outflows" value={`− ${formatAmount(numerics.expensesMTD)}`} />
                <div className="border-t pt-2 mt-1">
                  <DetailRow label="Operating Cash Flow" value={formatAmount(numerics.netProfit)} bold />
                </div>
              </>
            )}
            {kpiKey === "burn-rate" && (
              <>
                <DetailRow label="Total Burn (Period)" value={formatAmount(numerics.netCashFlowQuarter)} />
                <DetailRow label={`÷ ${periodMonths} ${periodMonths === 1 ? "month" : "months"}`} value={""} />
                <div className="border-t pt-2 mt-1">
                  <DetailRow label="Monthly Burn Rate" value={numerics.burnRate === 0 ? "Cash flow positive" : `${formatAmount(numerics.burnRate)}/mo`} bold />
                </div>
              </>
            )}
            {kpiKey === "runway" && (
              <>
                <DetailRow label="Cash Balance" value={formatAmount(numerics.cashOnHand)} />
                <DetailRow label="Monthly Burn Rate" value={numerics.burnRate === 0 ? "Positive" : `${formatAmount(numerics.burnRate)}/mo`} />
                <div className="border-t pt-2 mt-1">
                  <DetailRow label="Runway" value={numerics.burnRate === 0 ? "Unlimited" : `${numerics.runwayMonths.toFixed(1)} months`} bold />
                </div>
              </>
            )}
            {kpiKey === "net-cash-flow" && (
              <DetailRow label="Net Cash Flow" value={formatAmount(numerics.netCashFlow)} />
            )}
          </div>
          <p className="text-xs text-muted-foreground">Period: {period}</p>
        </div>
      );
    }

    // ── Cash on Hand: bank account breakdown ──
    if (kpiKey === "cash-on-hand") {
      return (
        <div className="space-y-3">
          <FormulaBox formula={getKpiFormula(kpiKey, period)} />
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : bankAccounts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No bank accounts connected.</p>
          ) : (
            <div className="space-y-2">
              {bankAccounts.map((acc, i) => (
                <DetailRow
                  key={acc.id ?? i}
                  label={`${acc.name}${acc.last4Digits ? ` ····${acc.last4Digits}` : ""}`}
                  value={formatAmount(acc.bookBalance ?? 0)}
                />
              ))}
              <div className="border-t pt-2 mt-1">
                <DetailRow label="Total" value={formatAmount(numerics.cashOnHand)} bold />
              </div>
            </div>
          )}
        </div>
      );
    }

    // ── Total Receivables: customer breakdown ──
    if (kpiKey === "receivables") {
      if (loading) {
        return (
          <div className="flex items-center justify-center py-12">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        );
      }

      // Aggregate all invoices by customer using totalAmount (billed amount, not balance)
      const periodInvoices = invoices.filter((inv) => inv.status !== 'void');
      const customerMap = new Map<string, { total: number; invoices: typeof periodInvoices }>();
      periodInvoices.forEach((inv) => {
        const name = inv.customerName ?? "Unknown";
        const cur = customerMap.get(name) ?? { total: 0, invoices: [] };
        customerMap.set(name, { total: cur.total + (inv.totalAmount ?? 0), invoices: [...cur.invoices, inv] });
      });
      const byCustomer = [...customerMap.entries()].sort((a, b) => b[1].total - a[1].total);

      // Authoritative total comes from the server KPI
      const serverTotal = receivables?.totalReceivables.amount ?? 0;

      return (
        <div className="space-y-3">
          <FormulaBox formula={getKpiFormula("receivables", period)} />
          {byCustomer.length > 0 ? (
            <>
              <p className="text-xs text-muted-foreground">
                {periodInvoices.length} invoice{periodInvoices.length !== 1 ? "s" : ""} · click a customer to expand
              </p>
              <div className="space-y-1">
                {byCustomer.map(([customer, { total, invoices: custInvs }]) => {
                  const expanded = expandedCustomers.has(customer);
                  return (
                    <div key={customer} className="rounded-lg border overflow-hidden">
                      <button
                        onClick={() => toggleCustomer(customer)}
                        className="w-full flex items-center justify-between px-3 py-2.5 bg-muted/20 hover:bg-muted/40 transition-colors text-left group"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {expanded
                            ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                          <p className="text-sm font-semibold truncate">{customer}</p>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {custInvs.length} invoice{custInvs.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <p className="text-sm font-bold tabular-nums shrink-0 ml-4">{formatAmount(total)}</p>
                      </button>
                      {expanded && (
                        <div className="border-t divide-y">
                          {custInvs.map((inv, i) => (
                            <div key={inv.id ?? i} className="flex items-center justify-between px-4 py-2 bg-background text-xs">
                              <div className="min-w-0">
                                <span className="text-muted-foreground">
                                  {inv.invoiceNumber ? `#${inv.invoiceNumber}` : `Invoice ${i + 1}`}
                                </span>
                                {inv.issueDate && (
                                  <span className="text-muted-foreground/60 ml-2">
                                    Issued {format(parseISO(inv.issueDate), 'MMM d, yyyy')}
                                  </span>
                                )}
                                {inv.status && (() => {
                                  const pastDue = (inv.status === 'unpaid' || inv.status === 'partially_paid') && inv.dueDate && parseISO(inv.dueDate) < new Date();
                                  const color = inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700'
                                    : pastDue ? 'bg-red-100 text-red-700'
                                    : inv.status === 'partially_paid' ? 'bg-amber-100 text-amber-700'
                                    : inv.status === 'void' ? 'bg-muted text-muted-foreground'
                                    : 'bg-orange-100 text-orange-700';
                                  const label = inv.status.replace(/_/g, ' ');
                                  return <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium ${color}`}>{label}</span>;
                                })()}
                              </div>
                              <p className="font-semibold tabular-nums shrink-0 ml-4">{formatAmount(inv.totalAmount ?? 0)}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="text-xs text-muted-foreground">No invoices found for this period.</p>
          )}
          <div className="border-t pt-2">
            <DetailRow label="Total Billed" value={formatAmount(serverTotal)} bold />
          </div>
        </div>
      );
    }

    // ── At Risk / Stuck > 30 days / Current ──
    if (kpiKey === "at-risk" || kpiKey === "stuck-30" || kpiKey === "current") {
      const asOfD = new Date(endDate); asOfD.setHours(0, 0, 0, 0);
      const getDays = (inv: Invoice) => {
        try { return Math.max(0, differenceInDays(asOfD, parseISO(inv.dueDate!))); } catch { return 0; }
      };

      const baseInvoices = kpiKey === "stuck-30"
        ? invoices.filter(inv => inv.dueDate && getDays(inv) > 30)
        : kpiKey === "at-risk"
          ? invoices.filter(inv => inv.dueDate && getDays(inv) > 0 && getDays(inv) <= 30)
          : invoices.filter(inv => { if (!inv.dueDate) return true; try { return parseISO(inv.dueDate) >= asOfD; } catch { return false; } });

      const uniqueCustomers = Array.from(new Set(baseInvoices.map(inv => inv.customerName ?? "Unknown"))).sort();
      const filtered = atRiskCustomer === "all"
        ? baseInvoices
        : baseInvoices.filter(inv => (inv.customerName ?? "Unknown") === atRiskCustomer);

      // Aggregate by customer
      const aggMap = new Map<string, { customer: string; count: number; amount: number; maxDays: number }>();
      filtered.forEach(inv => {
        const name = inv.customerName ?? "Unknown";
        const days = getDays(inv);
        const cur = aggMap.get(name) ?? { customer: name, count: 0, amount: 0, maxDays: 0 };
        aggMap.set(name, { ...cur, count: cur.count + 1, amount: cur.amount + (inv.balance ?? 0), maxDays: Math.max(cur.maxDays, days) });
      });

      const sortedAgg = [...aggMap.values()].sort((a, b) => {
        const d = aggSort.dir === "asc" ? 1 : -1;
        if (aggSort.field === "customer") return d * a.customer.localeCompare(b.customer);
        if (aggSort.field === "count") return d * (a.count - b.count);
        if (aggSort.field === "days") return d * (a.maxDays - b.maxDays);
        return d * (a.amount - b.amount);
      });

      const sortedInv = [...filtered].sort((a, b) => {
        const d = invSort.dir === "asc" ? 1 : -1;
        if (invSort.field === "customer") return d * ((a.customerName ?? "").localeCompare(b.customerName ?? ""));
        if (invSort.field === "invoice") return d * ((a.invoiceNumber ?? "") < (b.invoiceNumber ?? "") ? -1 : 1);
        if (invSort.field === "days") return d * (getDays(a) - getDays(b));
        return d * ((a.balance ?? 0) - (b.balance ?? 0));
      });

      const total = filtered.reduce((s, inv) => s + (inv.balance ?? 0), 0);

      const SortIcon = ({ active, dir }: { active: boolean; dir: "asc" | "desc" }) =>
        active
          ? (dir === "asc" ? <ArrowUp className="h-3 w-3 shrink-0" /> : <ArrowDown className="h-3 w-3 shrink-0" />)
          : <ArrowUpDown className="h-3 w-3 shrink-0 opacity-30" />;

      const toggleAgg = (field: typeof aggSort.field) =>
        setAggSort(prev => ({ field, dir: prev.field === field && prev.dir === "desc" ? "asc" : "desc" }));
      const toggleInv = (field: typeof invSort.field) =>
        setInvSort(prev => ({ field, dir: prev.field === field && prev.dir === "desc" ? "asc" : "desc" }));

      const hdrCls = "flex items-center gap-0.5 hover:text-foreground transition-colors";

      return (
        <div className="space-y-3">
          <FormulaBox formula={getKpiFormula(kpiKey, period)} />
          {/* Controls */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex rounded-lg border bg-muted/40 p-0.5 gap-0.5">
              <button onClick={() => setAtRiskGrouped(true)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${atRiskGrouped ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                By Customer
              </button>
              <button onClick={() => setAtRiskGrouped(false)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${!atRiskGrouped ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                By Invoice
              </button>
            </div>
            <select value={atRiskCustomer} onChange={e => setAtRiskCustomer(e.target.value)}
              className="text-xs border rounded-md px-2 py-1.5 bg-background flex-1 min-w-0 max-w-[200px] truncate">
              <option value="all">All Customers</option>
              {uniqueCustomers.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <p className="text-xs text-muted-foreground">
            {kpiKey === "at-risk"
              ? `${filtered.length} invoice${filtered.length !== 1 ? "s" : ""} · 1–30 days past due`
              : kpiKey === "current"
                ? `${filtered.length} invoice${filtered.length !== 1 ? "s" : ""} · not yet due`
                : `${filtered.length} invoice${filtered.length !== 1 ? "s" : ""} · overdue >30 days`}
            {atRiskCustomer !== "all" && <span className="font-medium"> · {atRiskCustomer}</span>}
          </p>

          {/* Aggregated by customer */}
          {atRiskGrouped ? (
            <div>
              <div className="grid text-[10px] font-semibold text-muted-foreground uppercase tracking-wide px-1 pb-1 gap-2 border-b"
                style={{ gridTemplateColumns: "1fr 28px 80px 44px" }}>
                <button onClick={() => toggleAgg("customer")} className={hdrCls}>
                  Customer <SortIcon active={aggSort.field === "customer"} dir={aggSort.dir} />
                </button>
                <button onClick={() => toggleAgg("count")} className={`${hdrCls} justify-end`}>
                  <SortIcon active={aggSort.field === "count"} dir={aggSort.dir} />#
                </button>
                <button onClick={() => toggleAgg("amount")} className={`${hdrCls} justify-end`}>
                  <SortIcon active={aggSort.field === "amount"} dir={aggSort.dir} />Balance
                </button>
                <button onClick={() => toggleAgg("days")} className={`${hdrCls} justify-end`}>
                  <SortIcon active={aggSort.field === "days"} dir={aggSort.dir} />Days
                </button>
              </div>
              {sortedAgg.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2 px-1">No items to show.</p>
              ) : sortedAgg.map((row, i) => (
                <div key={i} className="grid px-1 py-2 items-center gap-2 border-b last:border-b-0"
                  style={{ gridTemplateColumns: "1fr 28px 80px 44px" }}>
                  <p className="text-sm font-medium truncate" title={row.customer}>{row.customer}</p>
                  <p className="text-xs text-muted-foreground text-right tabular-nums">{row.count}</p>
                  <p className="text-sm font-semibold text-right tabular-nums">{formatAmount(row.amount)}</p>
                  <p className="text-xs text-muted-foreground text-right tabular-nums">{row.maxDays}d</p>
                </div>
              ))}
            </div>
          ) : (
            /* Individual invoice view */
            <div>
              <div className="grid text-[10px] font-semibold text-muted-foreground uppercase tracking-wide px-1 pb-1 gap-2 border-b"
                style={{ gridTemplateColumns: "1fr 80px 76px 48px" }}>
                <button onClick={() => toggleInv("customer")} className={hdrCls}>
                  Customer <SortIcon active={invSort.field === "customer"} dir={invSort.dir} />
                </button>
                <button onClick={() => toggleInv("invoice")} className={`${hdrCls} justify-end`}>
                  <SortIcon active={invSort.field === "invoice"} dir={invSort.dir} />Inv #
                </button>
                <button onClick={() => toggleInv("amount")} className={`${hdrCls} justify-end`}>
                  <SortIcon active={invSort.field === "amount"} dir={invSort.dir} />Balance
                </button>
                <button onClick={() => toggleInv("days")} className={`${hdrCls} justify-end`}>
                  <SortIcon active={invSort.field === "days"} dir={invSort.dir} />{kpiKey === "current" ? "Due In" : "Days"}
                </button>
              </div>
              {sortedInv.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2 px-1">No items to show.</p>
              ) : sortedInv.map((inv, i) => {
                const daysOverdue = getDays(inv);
                const daysUntilDue = inv.dueDate ? Math.max(0, differenceInDays(parseISO(inv.dueDate), asOfD)) : null;
                return (
                  <div key={inv.id ?? i} className="grid px-1 py-2 items-center gap-2 border-b last:border-b-0"
                    style={{ gridTemplateColumns: "1fr 80px 76px 48px" }}>
                    <p className="text-sm font-medium truncate" title={inv.customerName ?? undefined}>{inv.customerName ?? "—"}</p>
                    <p className="text-xs text-muted-foreground text-right tabular-nums whitespace-nowrap">{inv.invoiceNumber ? `#${inv.invoiceNumber}` : "—"}</p>
                    <p className="text-sm font-semibold text-right tabular-nums">{formatAmount(inv.balance ?? 0)}</p>
                    <p className="text-xs text-muted-foreground text-right tabular-nums">
                      {kpiKey === "current"
                        ? (daysUntilDue !== null ? `${daysUntilDue}d` : "—")
                        : `${daysOverdue}d`}
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          <div className="border-t pt-2">
            <DetailRow
              label={atRiskCustomer === "all"
                ? (kpiKey === "stuck-30" ? "Total overdue" : kpiKey === "current" ? "Total current" : "Total at risk")
                : `Total · ${atRiskCustomer}`}
              value={formatAmount(
                atRiskCustomer === "all"
                  ? (kpiKey === "stuck-30"
                    ? (agedAR?.overdue.amount ?? total)
                    : kpiKey === "current"
                      ? (agedAR?.current.amount ?? total)
                      : (agedAR?.atRisk.amount ?? total))
                  : total
              )}
              bold
            />
          </div>
        </div>
      );
    }

    // ── Open AR: combined current + at-risk + overdue ──
    if (kpiKey === "open-ar") {
      if (loading) {
        return (
          <div className="flex items-center justify-center py-12">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        );
      }

      const asOfD = new Date(endDate); asOfD.setHours(0, 0, 0, 0);
      const getDays = (inv: Invoice) => {
        try { return Math.max(0, differenceInDays(asOfD, parseISO(inv.dueDate!))); } catch { return 0; }
      };

      const buckets = {
        current: invoices.filter(inv => { try { return parseISO(inv.dueDate!) >= asOfD; } catch { return !inv.dueDate; } }),
        atRisk: invoices.filter(inv => inv.dueDate && getDays(inv) > 0 && getDays(inv) <= 30),
        overdue: invoices.filter(inv => inv.dueDate && getDays(inv) > 30),
      };

      const bucketMeta = [
        { key: "current" as const, label: "Current", labelCls: "text-emerald-700", bgCls: "bg-emerald-50 border-emerald-200" },
        { key: "atRisk" as const, label: "At Risk", labelCls: "text-red-700", bgCls: "bg-red-50 border-red-200" },
        { key: "overdue" as const, label: "Overdue >30d", labelCls: "text-amber-700", bgCls: "bg-amber-50 border-amber-200" },
      ];

      const totalAR = invoices.reduce((s, inv) => s + (inv.balance ?? 0), 0);

      return (
        <div className="space-y-4">
          <FormulaBox formula={getKpiFormula("open-ar", period)} />
          <div className="grid grid-cols-3 gap-2">
            {bucketMeta.map(({ key, label, labelCls, bgCls }) => (
              <div key={key} className={`rounded-lg border ${bgCls} p-2.5 text-center`}>
                <p className={`text-[10px] font-semibold uppercase tracking-wide ${labelCls}`}>{label}</p>
                <p className={`text-base font-bold tabular-nums ${labelCls}`}>{formatAmount(buckets[key].reduce((s, inv) => s + (inv.balance ?? 0), 0))}</p>
                <p className={`text-[10px] ${labelCls} opacity-70`}>{buckets[key].length} inv.</p>
              </div>
            ))}
          </div>
          {invoices.length > 0 ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">{invoices.length} invoice{invoices.length !== 1 ? "s" : ""}</p>
                <p className="text-xs font-semibold">{formatAmount(agedAR?.total.amount ?? totalAR)} total</p>
              </div>
              {invoices.map(inv => {
                const days = getDays(inv);
                const bucket = days > 30 ? bucketMeta[2] : days > 0 ? bucketMeta[1] : bucketMeta[0];
                return (
                  <div key={inv.id} className="flex items-center justify-between py-2 border-b gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{inv.customerName ?? "Unknown"}</p>
                      <p className="text-xs text-muted-foreground">{inv.invoiceNumber ?? inv.id}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold tabular-nums">{formatAmount(inv.balance ?? 0)}</p>
                      <span className={`text-[10px] font-medium ${bucket.labelCls}`}>{bucket.label}</span>
                    </div>
                  </div>
                );
              })}
              <div className="flex items-center justify-between pt-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Total</p>
                <p className="text-sm font-bold tabular-nums">{formatAmount(agedAR?.total.amount ?? totalAR)}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No outstanding invoices</p>
          )}
        </div>
      );
    }

    // ── Expenses: bank outflow transactions ──
    if (kpiKey === "expenses") {
      const LIMIT = 200;
      const isTruncated = detailCashOutflows.length === LIMIT;
      return (
        <div className="space-y-3">
          <FormulaBox formula={getKpiFormula("expenses", period)} />
          <p className="text-xs text-muted-foreground">
            {period}
            {isTruncated
              ? ` · showing first ${LIMIT} transactions`
              : ` · ${detailCashOutflows.length} transaction${detailCashOutflows.length !== 1 ? "s" : ""}`}
          </p>
          {detailCashOutflows.length === 0 && (
            <p className="text-sm text-muted-foreground py-4">No operational outflows found for this period.</p>
          )}
          <div className="divide-y border-y">
            {detailCashOutflows.map((row) => (
              <div key={row.id} className="py-3 px-1">
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{row.merchantName || row.description || "—"}</p>
                    <p className="text-xs text-muted-foreground">
                      {(() => { try { return format(parseISO(String(row.transactionDate)), "MMM d, yyyy"); } catch { return String(row.transactionDate); } })()}
                      {row.bankAccountName ? ` · ${row.bankAccountName}` : ""}
                    </p>
                    {row.category && (
                      <span className="inline-block mt-0.5 text-[10px] font-medium px-1.5 py-px rounded bg-muted text-muted-foreground">
                        {row.category}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-bold shrink-0 text-rose-700">{formatAmount(row.amount)}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t pt-2">
            <DetailRow label="Total" value={formatAmount(numerics.expensesMTD)} bold />
          </div>
        </div>
      );
    }

    // ── Invoice list KPIs (revenue, collected, avg-days-collect, receivables) ──
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    // revenue — operational bank inflows (direction=IN, excluding transfers/loans/financing)
    if (kpiKey === "revenue") {
      const LIMIT = 200;
      const isTruncated = detailCashInflows.length === LIMIT;
      return (
        <div className="space-y-3">
          <FormulaBox formula={getKpiFormula("revenue", period)} />
          <p className="text-xs text-muted-foreground">
            {period}
            {isTruncated
              ? ` · showing first ${LIMIT} transactions`
              : ` · ${detailCashInflows.length} transaction${detailCashInflows.length !== 1 ? "s" : ""}`}
          </p>
          {detailCashInflows.length === 0 && (
            <p className="text-sm text-muted-foreground py-4">No operational inflows found for this period.</p>
          )}
          <div className="divide-y border-y">
            {detailCashInflows.map((row) => (
              <div key={row.id} className="py-3 px-1">
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{row.merchantName || row.description || "—"}</p>
                    <p className="text-xs text-muted-foreground">
                      {(() => { try { return format(parseISO(String(row.transactionDate)), "MMM d, yyyy"); } catch { return String(row.transactionDate); } })()}
                      {row.bankAccountName ? ` · ${row.bankAccountName}` : ""}
                    </p>
                    {row.category && (
                      <span className="inline-block mt-0.5 text-[10px] font-medium px-1.5 py-px rounded bg-muted text-muted-foreground">
                        {row.category}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-bold shrink-0 text-green-700">{formatAmount(row.amount)}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t pt-2">
            <DetailRow label="Total" value={formatAmount(numerics.revenueMTD)} bold />
          </div>
        </div>
      );
    }

    // collected — invoices that received payments in the period, keyed by payment date
    if (kpiKey === "collected") {
      const collectedTotal = collectedInvoices.reduce((s, r) => s + r.paidInPeriod, 0);
      const statusLabel = (s: string) => s.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      const statusClass = (s: string) => {
        const normalized = s.toLowerCase();
        if (normalized === 'paid') return 'bg-green-100 text-green-800';
        if (normalized === 'partially_paid') return 'bg-blue-100 text-blue-800';
        if (normalized === 'unpaid') return 'bg-orange-100 text-orange-800';
        return 'bg-gray-100 text-gray-700';
      };
      const fmtMethod = (m: string) =>
        m.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').replace(/\s+/g, ' ').trim()
          .replace(/\b\w/g, c => c.toUpperCase());
      return (
        <div className="space-y-3">
          <FormulaBox formula={getKpiFormula("collected", period)} />
          <p className="text-xs text-muted-foreground">
            {period} · {collectedInvoices.length} invoice{collectedInvoices.length !== 1 ? "s" : ""}
          </p>
          {collectedInvoices.length === 0 && (
            <p className="text-sm text-muted-foreground py-4">No payments received in this period.</p>
          )}
          <div className="divide-y border-y">
            {collectedInvoices.slice(0, 50).map((row, i) => (
              <div key={row.invoiceId ?? i} className="py-3 px-1">
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{row.customerName}</p>
                    <p className="text-xs text-muted-foreground">
                      {row.invoiceNumber ? `#${row.invoiceNumber}` : ""}
                      {row.issueDate ? (() => { const d = parseISO(row.issueDate); return isValid(d) ? ` · ${format(d, "MMM d, yyyy")}` : ""; })() : ""}
                    </p>
                    {(row.payments ?? []).length > 0 && (
                      <div className="mt-1.5 space-y-0.5">
                        {(row.payments ?? []).map((pmt, pi) => (
                          <p key={pi} className="text-xs text-muted-foreground bg-muted/50 rounded px-1.5 py-0.5 border-l-2 border-muted-foreground/20">
                            {(() => { const d = parseISO(pmt.date); return isValid(d) ? format(d, "MMM d, yyyy") : pmt.date; })()}
                            {pmt.method ? ` · ${fmtMethod(pmt.method)}` : ""}
                            {` · ${formatAmount(pmt.amount, row.currency)}`}
                          </p>
                        ))}
                      </div>
                    )}
                    <span className={`inline-block mt-1 text-[10px] font-semibold px-1.5 py-px rounded ${statusClass(row.invoiceStatus)}`}>
                      {statusLabel(row.invoiceStatus)}
                    </span>
                  </div>
                  <p className="text-sm font-bold shrink-0">{formatAmount(row.paidInPeriod, row.currency)}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t pt-2">
            <DetailRow label="Total" value={formatAmount(collectedTotal)} bold />
          </div>
        </div>
      );
    }

    if (invoices.length === 0) {
      return <p className="text-sm text-muted-foreground py-4">No invoices found for this period.</p>;
    }

    const totalShown = invoices.reduce((s, inv) => s + (inv.totalAmount ?? 0), 0);

    if (kpiKey === "avg-days-collect") {
      return (
        <div className="space-y-3">
          <FormulaBox formula={getKpiFormula("avg-days-collect", period)} />
          <p className="text-xs text-muted-foreground">
            {period} · {invoices.length} paid invoice{invoices.length !== 1 ? "s" : ""} · weighted average
            {numerics.avgDaysCount >= 3 && ` · ${numerics.avgDays.toFixed(0)} days`}
          </p>
          <div className="space-y-2">
            {invoices.slice(0, 50).map((inv, i) => {
              let daysLabel = "—";
              try {
                const days = differenceInDays(parseISO(inv.dueDate), parseISO(inv.issueDate));
                daysLabel = `${Math.max(0, days)} days`;
              } catch { /* invalid date */ }
              return (
                <div key={inv.id ?? i} className="rounded-lg border bg-muted/20 p-3">
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{inv.customerName}</p>
                      <p className="text-xs text-muted-foreground">
                        {inv.invoiceNumber ? `#${inv.invoiceNumber} · ` : ""}
                        {inv.issueDate ? format(parseISO(inv.issueDate), "MMM d, yyyy") : "—"}
                        {" → "}
                        {inv.dueDate ? format(parseISO(inv.dueDate), "MMM d") : "—"}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold">{daysLabel}</p>
                      <p className="text-xs text-muted-foreground">{formatAmount(inv.totalAmount ?? 0, inv.currency)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // revenue, receivables
    return (
      <div className="space-y-3">
        <FormulaBox formula={getKpiFormula(kpiKey, period)} />
        <p className="text-xs text-muted-foreground">
          {period} · {invoices.length} invoice{invoices.length !== 1 ? "s" : ""}
        </p>
        <div className="space-y-2">
          {invoices.slice(0, 50).map((inv, i) => (
            <div key={inv.id ?? i} className="rounded-lg border bg-muted/20 p-3">
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{inv.customerName}</p>
                  <p className="text-xs text-muted-foreground">
                    {inv.invoiceNumber ? `#${inv.invoiceNumber} · ` : ""}
                    {inv.issueDate ? format(parseISO(inv.issueDate), "MMM d, yyyy") : "—"}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold">{formatAmount(inv.totalAmount ?? 0, inv.currency)}</p>
                  {inv.balance !== undefined && inv.balance !== inv.totalAmount && (
                    <p className="text-xs text-muted-foreground">Bal: {formatAmount(inv.balance, inv.currency)}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="border-t pt-2">
          <DetailRow
            label="Total"
            value={formatAmount(kpiKey === "revenue" ? numerics.revenueMTD : totalShown)}
            bold
          />
        </div>
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full max-w-md overflow-y-auto" aria-describedby={undefined}>
        {kpiKey && KPI_NAV_TARGET[kpiKey] && onNavigate && (
          <button
            onClick={() => { onNavigate(KPI_NAV_TARGET[kpiKey]!); onClose(); }}
            className="absolute right-10 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            title={`Open in ${KPI_NAV_TARGET[kpiKey]}`}
          >
            <ExternalLink className="h-4 w-4" />
          </button>
        )}
        <SheetHeader>
          <SheetTitle>{label}</SheetTitle>
        </SheetHeader>
        <div className="mt-4">{renderContent()}</div>
      </SheetContent>
    </Sheet>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

interface FinancialControlPanelProps {
  onNavigate?: (tab: string) => void;
  view?: "kpis" | "for-you";
  dateFilterKey: DateFilterKey;
  dateCustomFrom?: Date;
  dateCustomTo?: Date;
  onDateFilterChange?: (key: DateFilterKey | '', from?: Date, to?: Date) => void;
}

const FinancialControlPanel = ({ onNavigate, view = "kpis", dateFilterKey, dateCustomFrom, dateCustomTo, onDateFilterChange }: FinancialControlPanelProps) => {
  const [cashflow, setCashflow] = useState<CashflowKPIs | null>(null);
  const [business, setBusiness] = useState<BusinessOverviewKPIs | null>(null);
  const [signals, setSignals] = useState<FinancialSignal[]>([]);
  const [receivables, setReceivables] = useState<ReceivablesKPIs | null>(null);
  const [agedAR, setAgedAR] = useState<AgedReceivablesKPIs | null>(null);
  const [agedARLive, setAgedARLive] = useState<AgedReceivablesKPIs | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);

  // Per-section loading states — each section renders independently as data arrives
  const [cashflowLoading, setCashflowLoading] = useState(true);
  const [businessLoading, setBusinessLoading] = useState(true);
  const [receivablesLoading, setReceivablesLoading] = useState(true);
  const [agedARLoading, setAgedARLoading] = useState(true);
  const [agedARLiveLoading, setAgedARLiveLoading] = useState(true);
  const [signalsLoading, setSignalsLoading] = useState(true);

  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [qbSyncedAt, setQbSyncedAt] = useState<Date | null>(null);
  const [kpiDetailKey, setKpiDetailKey] = useState<KpiDetailKey | null>(null);
  const [detailInvoices, setDetailInvoices] = useState<Invoice[]>([]);
  const [detailCollectedInvoices, setDetailCollectedInvoices] = useState<CollectedInvoiceRow[]>([]);
  const [detailCashInflows, setDetailCashInflows] = useState<CashInflowRow[]>([]);
  const [detailCashOutflows, setDetailCashOutflows] = useState<CashOutflowRow[]>([]);
  const [detailBankAccounts, setDetailBankAccounts] = useState<BankAccountRow[]>([]);
  const [detailExpenses, setDetailExpenses] = useState<ExpensePayableItem[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const resolvedRange = useMemo(
    () => resolveDateFilter(dateFilterKey, dateCustomFrom, dateCustomTo) ?? fallbackRange(),
    [dateFilterKey, dateCustomFrom, dateCustomTo],
  );
  const resolvedRangeRef = useRef(resolvedRange);
  resolvedRangeRef.current = resolvedRange;
  const cardRef = useRef<HTMLDivElement>(null);

  /** Fire each KPI endpoint independently — UI renders each section as it resolves. */
  const fetchRangeKPIs = useCallback((range: { startDate: string; endDate: string }) => {
    setCashflowLoading(true);
    fetchCashflowKPIs(range)
      .then(data => setCashflow(data))
      .catch((err) => { captureError(err, { kpi_type: 'cashflow', period_start: range.startDate, period_end: range.endDate }); setCashflow(null); })
      .finally(() => setCashflowLoading(false));

    setBusinessLoading(true);
    fetchBusinessKPIs(range)
      .then(data => setBusiness(data))
      .catch((err) => { captureError(err, { kpi_type: 'business_overview', period_start: range.startDate, period_end: range.endDate }); setBusiness(null); })
      .finally(() => setBusinessLoading(false));

    setReceivablesLoading(true);
    fetchReceivablesKPIs(range)
      .then(data => setReceivables(data ?? null))
      .catch((err) => { captureError(err, { kpi_type: 'receivables', period_start: range.startDate, period_end: range.endDate }); setReceivables(null); })
      .finally(() => setReceivablesLoading(false));

    setAgedARLoading(true);
    fetchAgedReceivablesKPIs(range)
      .then(data => setAgedAR(data ?? null))
      .catch((err) => { captureError(err, { kpi_type: 'aged_receivables', period_start: range.startDate, period_end: range.endDate }); setAgedAR(null); })
      .finally(() => setAgedARLoading(false));
  }, []);

  const fetchAll = useCallback((isManual = false) => {
    const range = resolvedRangeRef.current;
    fetchRangeKPIs(range);
    if (isManual) posthog.capture("financial_signals_refreshed");

    // Live AR — always as of today regardless of selected period
    const liveDate = format(new Date(), 'yyyy-MM-dd');
    setAgedARLiveLoading(true);
    fetchAgedReceivablesKPIs({ startDate: liveDate, endDate: liveDate })
      .then(data => setAgedARLive(data ?? null))
      .catch((err) => { captureError(err, { kpi_type: 'aged_receivables_live' }); setAgedARLive(null); })
      .finally(() => setAgedARLiveLoading(false));

    setSignalsLoading(true);
    apiClient.get<SignalsResult>("/kpi/signals")
      .then(data => setSignals(data.signals))
      .catch((err) => { captureError(err, { kpi_type: 'financial_signals' }); setSignals([]); })
      .finally(() => setSignalsLoading(false));

    apiClient.get<Account[]>("/accounts")
      .then(accs => { setAccounts(accs ?? []); setRefreshedAt(new Date()); })
      .catch((err) => captureError(err, { context: 'accounts_load' }));

    getSyncStatus()
      .then(status => { const c = status?.lastSync?.completedAt; if (c) setQbSyncedAt(new Date(c)); })
      .catch(() => {});
  }, [fetchRangeKPIs]);

  useEffect(() => {
    fetchAll();
    posthog.capture("financial_panel_viewed");
  }, [fetchAll]);

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    fetchRangeKPIs(resolvedRange);
  }, [resolvedRange, fetchRangeKPIs]);

  /** Open the KPI detail sidebar, fetching invoice or expense data when needed. */
  const openKpiDetail = useCallback(async (key: KpiDetailKey) => {
    setKpiDetailKey(key);
    posthog.capture("kpi_detail_opened", { kpi: key });
    setDetailInvoices([]);
    setDetailCollectedInvoices([]);
    setDetailCashInflows([]);
    setDetailCashOutflows([]);
    setDetailExpenses([]);

    const fetchInvoices = async (params: Record<string, string | number | boolean>) => {
      setDetailLoading(true);
      try {
        const res = await apiClient.get<Invoice[]>("/invoices", { limit: 100, page: 1, ...params });
        setDetailInvoices(Array.isArray(res) ? res : ((res as { items?: Invoice[] }).items ?? []));
      } catch { setDetailInvoices([]); }
      finally { setDetailLoading(false); }
    };

    const fetchExpenses = async (params: Record<string, string | number | boolean>) => {
      setDetailLoading(true);
      try {
        const res = await apiClient.get<ExpensePayableItem[]>("/expenses-payable", { limit: 200, page: 1, ...params });
        setDetailExpenses(Array.isArray(res) ? res : ((res as { items?: ExpensePayableItem[] }).items ?? []));
      } catch { setDetailExpenses([]); }
      finally { setDetailLoading(false); }
    };

    const { startDate, endDate } = resolvedRangeRef.current;
    if (key === "avg-days-collect") {
      await fetchInvoices({ status: "paid", dateFrom: startDate, dateTo: endDate });
    } else if (key === "revenue") {
      setDetailLoading(true);
      try {
        const rows = await fetchCashInflowDetails({ startDate, endDate });
        setDetailCashInflows(Array.isArray(rows) ? rows : []);
      } catch { setDetailCashInflows([]); }
      finally { setDetailLoading(false); }
    } else if (key === "collected") {
      setDetailLoading(true);
      try {
        const list = await apiClient.get<CollectedInvoiceRow[]>(
          "/customer-payments/collected-invoices",
          { dateFrom: startDate, dateTo: endDate },
        );
        setDetailCollectedInvoices(list);
      } catch { setDetailCollectedInvoices([]); }
      finally { setDetailLoading(false); }
    } else if (key === "receivables") {
      await fetchInvoices({ issueDateFrom: startDate, issueDateTo: endDate, limit: 200 });
    } else if (key === "at-risk" || key === "stuck-30") {
      await fetchInvoices({ status: "unpaid,partially_paid", limit: 500, page: 1 });
    } else if (key === "current") {
      await fetchInvoices({ status: "unpaid,partially_paid", limit: 200, page: 1 });
    } else if (key === "open-ar") {
      await fetchInvoices({ status: "unpaid,partially_paid", limit: 500, page: 1 });
    } else if (key === "expenses") {
      setDetailLoading(true);
      try {
        const rows = await fetchCashOutflowDetails({ startDate, endDate });
        setDetailCashOutflows(Array.isArray(rows) ? rows : []);
      } catch { setDetailCashOutflows([]); }
      finally { setDetailLoading(false); }
    }
    if (key === "cash-on-hand") {
      setDetailLoading(true);
      try {
        const rows = await apiClient.get<BankAccountRow[]>("/banking/banks");
        const bankOnly = (Array.isArray(rows) ? rows : []).filter(
          (a) => a.type?.toUpperCase() !== "CREDIT"
        );
        setDetailBankAccounts(bankOnly);
      } catch { setDetailBankAccounts([]); }
      finally { setDetailLoading(false); }
    }
    // calculation KPIs (cash-net-profit, burn-rate etc.) → use numerics only
  }, []);

  const handleExportPng = useCallback(async () => {
    if (!cardRef.current || exporting) return;
    try {
      setExporting(true);
      const dataUrl = await toPng(cardRef.current, { quality: 0.95, pixelRatio: 2 });
      const link = document.createElement("a");
      link.download = `financial-panel-${dateFilterKey}-${new Date().toISOString().split("T")[0]}.png`;
      link.href = dataUrl;
      link.click();
      posthog.capture("financial_panel_exported", { format: "png", period: dateFilterKey });
    } catch (e) { console.error("Export PNG failed", e); captureError(e, { context: 'export_png', period: dateFilterKey }); }
    finally { setExporting(false); }
  }, [dateFilterKey, exporting]);

  const handleExportPdf = useCallback(async () => {
    if (!cardRef.current || exporting) return;
    try {
      setExporting(true);
      const dataUrl = await toPng(cardRef.current, { quality: 0.95, pixelRatio: 2 });
      const win = window.open("");
      if (!win) return;
      win.document.write(
        `<!DOCTYPE html><html><head><title>Financial Control Panel — ${dateFilterKey}</title>` +
        `<style>*{margin:0;padding:0;box-sizing:border-box}body{background:#fff}img{max-width:100%;display:block}@page{size:auto;margin:10mm}</style>` +
        `</head><body><img src="${dataUrl}"/></body></html>`
      );
      win.document.close();
      posthog.capture("financial_panel_exported", { format: "pdf", period: dateFilterKey });
      setTimeout(() => { win.print(); }, 600);
    } catch (e) { console.error("Export PDF failed", e); captureError(e, { context: 'export_pdf', period: dateFilterKey }); }
    finally { setExporting(false); }
  }, [dateFilterKey, exporting]);

  // ── Values from server — all calculations are server-side ──────────────────

  const hasBankData = cashflow?.hasBankData ?? false;
  const netCashFlow = cashflow?.netCashFlowMonth.value ?? 0;
  const revenueMTD = business?.revenueMTD.value ?? 0;
  const expensesMTD = business?.expensesTotal.value ?? 0;
  const netProfit = business?.netProfit.value ?? 0;
  const avgDays = business?.avgDaysToPay.value ?? 0;
  const avgDaysCount = business?.avgDaysToPay.count ?? 0;
  const cashOnHand = cashflow?.cashOnHand.value ?? 0;
  const burnRate = cashflow?.burnRate.value ?? 0;
  const runwayMonths = cashflow?.runway.value ?? 0;
  const nonOpCashFlow = cashflow?.nonOperatingCashFlow.value ?? 0;

  const periodLong = DATE_FILTER_OPTIONS.find((o) => o.key === dateFilterKey)?.label ?? dateFilterKey;
  const periodMonths = Math.max(1, Math.round(
    (new Date(resolvedRange.endDate).getTime() - new Date(resolvedRange.startDate).getTime()) / 86_400_000 / 30.44
  ));
  const netCashFlowQuarter = cashflow?.netCashFlowQuarter.value ?? 0;

  const burnLabel = !hasBankData ? "—" : burnRate === 0 ? "Positive" : `${formatAmount(burnRate)}/mo`;
  const burnSubtitle = !hasBankData ? "Connect a bank account" : burnRate === 0 ? "Cash flow positive" : `Avg monthly outflow · ${periodLong}`;
  const runwayLabel = !hasBankData ? "—" : burnRate === 0 ? "Unlimited" : `${runwayMonths.toFixed(1)} mo`;
  const runwaySubtitle = !hasBankData ? "Bank data required" : burnRate === 0 ? "Cash flow positive" : "At current burn rate";
  const runwayClass = !hasBankData ? "text-muted-foreground" : burnRate === 0 || runwayMonths >= 6 ? "text-emerald-600" : runwayMonths < 3 ? "text-red-600" : "text-amber-600";
  const avgDaysReady = !!business && avgDaysCount >= 3;
  const avgDaysLabel = !business ? "—" : avgDaysCount === 0 ? "No data" : `${avgDays.toFixed(0)} days`;
  const avgDaysSubtitle = !business ? "" : avgDaysCount === 0 ? "No payments recorded" : `Based on ${avgDaysCount} payment${avgDaysCount === 1 ? "" : "s"}${avgDaysCount < 3 ? " — low confidence" : ""}`;
  const avgDaysClass = !avgDaysReady ? "text-muted-foreground" : avgDays > 45 ? "text-red-600" : avgDays > 30 ? "text-amber-600" : "text-emerald-600";

  const financialSignals = signals.filter((s) => s.category !== "sync");
  const syncSignals = signals.filter((s) => s.category === "sync");
  const criticalOrHighCount = financialSignals.filter((s) => s.level === "critical" || s.level === "high").length;

  const qbSyncSignal = syncSignals[0] ?? null;
  const qbSyncStale = qbSyncedAt ? Date.now() - qbSyncedAt.getTime() > 12 * 60 * 60 * 1000 : false;

  const anyLoading = cashflowLoading || businessLoading || receivablesLoading || agedARLoading;

  const numericsForDetail = {
    revenueMTD, expensesMTD, netProfit,
    burnRate, netCashFlow, netCashFlowQuarter, runwayMonths, avgDays, avgDaysCount, cashOnHand,
  };

  return (
    <TooltipProvider delayDuration={200}>

      {/* ── Financial KPIs Panel ─────────────────────────────────────────────── */}
      {view === "kpis" && (
        <Card className="m-6 shadow-md">
          <CardHeader className="py-5 border-b">
            <div className="flex items-center justify-between gap-4">

              {/* Title */}
              <CardTitle className="text-[22px] font-bold tracking-tight shrink-0">
                Financial KPIs Panel
              </CardTitle>

              {/* Right-side controls: Sync → Export → Timeframe */}
              <div className="flex items-center gap-3">

                {/* Sync */}
                <button onClick={fetchAll} disabled={anyLoading} title={refreshedAt ? formatDistanceToNow(refreshedAt, { addSuffix: true }) : "Sync data"}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted hover:bg-muted/70 active:scale-95 transition-all text-sm font-medium text-muted-foreground hover:text-foreground disabled:opacity-40 group">
                  <RefreshCw className={`h-4 w-4 transition-transform duration-500 ${anyLoading ? "animate-spin" : "group-hover:rotate-180"}`} />
                  {anyLoading ? "Updating…" : "Sync"}
                </button>

                {/* Export */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button disabled={anyLoading || exporting} title="Export"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted hover:bg-muted/70 active:scale-95 transition-all text-sm font-medium text-muted-foreground hover:text-foreground disabled:opacity-40">
                      <Download className="h-4 w-4" />
                      <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuItem onClick={handleExportPng} className="gap-2 cursor-pointer">
                      <Download className="h-4 w-4" /> Save as PNG
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleExportPdf} className="gap-2 cursor-pointer">
                      <Download className="h-4 w-4" /> Print / PDF
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Timeframe */}
                {onDateFilterChange && (
                  <DateFilter
                    value={dateFilterKey}
                    customFrom={dateCustomFrom}
                    customTo={dateCustomTo}
                    onChange={onDateFilterChange}
                    label=""
                    width="w-[160px]"
                    showEmpty={false}
                  />
                )}

              </div>
            </div>
          </CardHeader>

          <div ref={cardRef} className="bg-card">
            <CardContent className="p-6 space-y-8">
              {/* ── Cash Position ────────────────────────────────────── */}
              <div>
                <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground/70 mb-3">
                  Cash Position
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Left: period-based KPIs */}
                  <div className="grid grid-cols-2 gap-3">
                    <SmartViewCard label="Total Billed"
                      value={receivables ? formatAmount(receivables.totalReceivables.amount) : "—"}
                      compactValue={receivables ? formatAmountCompact(receivables.totalReceivables.amount) : undefined}
                      countLabel={receivables ? `${receivables.totalReceivables.count} invoice${receivables.totalReceivables.count !== 1 ? "s" : ""}` : undefined}
                      subtitle={`All invoices issued · ${periodLong}`}
                      colorScheme="sky" loading={receivablesLoading}
                      tag={{ type: "period", text: periodLong }}
                      onClick={() => openKpiDetail("receivables")} />
                    <SmartViewCard label="Collected"
                      value={receivables ? formatAmount(receivables.collected.amount) : "—"}
                      compactValue={receivables ? formatAmountCompact(receivables.collected.amount) : undefined}
                      countLabel={receivables ? `${receivables.collected.count} invoice${receivables.collected.count !== 1 ? "s" : ""}` : undefined}
                      subtitle="Payments received this period"
                      colorScheme="purple" loading={receivablesLoading}
                      tag={{ type: "period", text: periodLong }}
                      onClick={() => openKpiDetail("collected")} />

                  </div>

                  {/* Right: Open Receivables summary + breakdown */}
                  <div className="flex flex-col gap-2">
                    {/* Account Receivables — period-based (left) + live today (right) */}
                    <div className="w-full rounded-xl border bg-amber-50 border-amber-200 shadow-sm flex overflow-hidden">
                      {/* Period-based AR — clickable, opens detail */}
                      <button onClick={() => openKpiDetail("open-ar")}
                        className="flex-1 p-4 text-left hover:bg-amber-100 transition-colors group cursor-pointer"
                      >
                        <div className="w-full flex items-center justify-between gap-1">
                          <p className="text-xs font-semibold uppercase tracking-wide leading-tight text-amber-600">Account Receivables</p>
                          <ArrowRight className="h-3 w-3 text-amber-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                        </div>
                        <div className="mt-1">
                          <span className="text-[10px] text-muted-foreground/80 flex items-center gap-0.5">
                            <Calendar className="h-2.5 w-2.5" />{periodLong}
                          </span>
                          <p className={`text-2xl font-bold tabular-nums leading-tight ${agedARLoading ? "text-muted-foreground" : "text-amber-700"}`}>
                            {agedARLoading ? "—" : agedAR ? formatAmount(agedAR.total.amount) : "—"}
                          </p>
                          <p className={`text-sm font-medium tabular-nums opacity-70 ${agedARLoading ? "text-muted-foreground" : "text-amber-700"}`}>
                            {agedARLoading ? "—" : agedAR ? `${agedAR.total.count} invoice${agedAR.total.count !== 1 ? "s" : ""}` : ""}
                          </p>
                        </div>
                      </button>

                      {/* Vertical divider */}
                      <div className="self-stretch w-px bg-amber-200/70" />

                      {/* Live / All-Time AR — always as of today */}
                      <div className="flex-1 p-4">
                        <div className="flex items-center gap-1.5">
                          <p className="text-xs font-semibold uppercase tracking-wide leading-tight text-amber-600">Live</p>
                          <span className="flex items-center gap-0.5 text-[9px] font-medium text-emerald-600">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" />
                            Now
                          </span>
                        </div>
                        <div className="mt-1">
                          <span className="text-[10px] text-muted-foreground/80">All outstanding today</span>
                          <p className={`text-2xl font-bold tabular-nums leading-tight ${agedARLiveLoading ? "text-muted-foreground" : "text-amber-700"}`}>
                            {agedARLiveLoading ? "—" : agedARLive ? formatAmount(agedARLive.total.amount) : "—"}
                          </p>
                          <p className={`text-sm font-medium tabular-nums opacity-70 ${agedARLiveLoading ? "text-muted-foreground" : "text-amber-700"}`}>
                            {agedARLiveLoading ? "—" : agedARLive ? `${agedARLive.total.count} invoice${agedARLive.total.count !== 1 ? "s" : ""}` : ""}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <SmartViewCard label="Current"
                        value={agedAR ? formatAmount(agedAR.current.amount) : "—"}
                        compactValue={agedAR ? formatAmountCompact(agedAR.current.amount) : undefined}
                        countLabel={agedAR ? `${agedAR.current.count} inv.` : undefined}
                        colorScheme="emerald" loading={agedARLoading}
                        tag={{ type: "period", text: periodLong }}
                        compact
                        onClick={() => openKpiDetail("current")} />
                      <SmartViewCard label="At Risk"
                        value={agedAR ? formatAmount(agedAR.atRisk.amount) : "—"}
                        compactValue={agedAR ? formatAmountCompact(agedAR.atRisk.amount) : undefined}
                        countLabel={agedAR ? `${agedAR.atRisk.count} inv.` : undefined}
                        colorScheme="red" loading={agedARLoading}
                        tag={{ type: "period", text: periodLong }}
                        badge="1–30d"
                        compact
                        onClick={() => openKpiDetail("at-risk")} />
                      <SmartViewCard label="Overdue"
                        value={agedAR ? formatAmount(agedAR.overdue.amount) : "—"}
                        compactValue={agedAR ? formatAmountCompact(agedAR.overdue.amount) : undefined}
                        countLabel={agedAR ? `${agedAR.overdue.count} inv.` : undefined}
                        colorScheme="amber" loading={agedARLoading}
                        tag={{ type: "period", text: periodLong }}
                        badge=">30d"
                        compact
                        onClick={() => openKpiDetail("stuck-30")} />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Operating Performance ────────────────────────────── */}
              <div className={businessLoading ? "opacity-60 transition-opacity" : "transition-opacity"}>
                <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground/70 mb-3">
                  Operating Cash Performance — {periodLong}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricCard label="Cash Inflows"
                    value={revenueMTD > 0 ? formatAmount(revenueMTD) : "—"}
                    compactValue={revenueMTD > 0 ? formatAmountCompact(revenueMTD) : undefined}
                    subtitle="Payments received" formula={getKpiFormula("revenue", periodLong)}
                    valueClass="text-green-600" trend={revenueMTD > 0 ? "up" : undefined}
                    periodTag={periodLong} isLoading={businessLoading}
                    onClick={() => openKpiDetail("revenue")} />
                  <MetricCard label="Cash Outflows"
                    value={expensesMTD > 0 ? formatAmount(expensesMTD) : "—"}
                    compactValue={expensesMTD > 0 ? formatAmountCompact(expensesMTD) : undefined}
                    subtitle="Vendor payments, payroll & expenses" formula={getKpiFormula("expenses", periodLong)}
                    valueClass="text-rose-600" periodTag={periodLong} isLoading={businessLoading}
                    onClick={() => openKpiDetail("expenses")} />
                  <MetricCard label="Operating Cash Flow"
                    value={revenueMTD > 0 || expensesMTD > 0 ? formatAmount(netProfit) : "—"}
                    compactValue={revenueMTD > 0 || expensesMTD > 0 ? formatAmountCompact(netProfit) : undefined}
                    subtitle="Cash Inflows − Cash Outflows"
                    formula={getKpiFormula("cash-net-profit", periodLong)}
                    valueClass={netProfit >= 0 ? "text-emerald-600" : "text-red-600"}
                    trend={revenueMTD > 0 || expensesMTD > 0 ? (netProfit > 0 ? "up" : netProfit < 0 ? "down" : "flat") : undefined}
                    periodTag={periodLong} isLoading={businessLoading}
                    onClick={() => openKpiDetail("cash-net-profit")} />
                  <MetricCard label="Average Days to Collect"
                    value={avgDaysLabel} subtitle={avgDaysSubtitle}
                    formula={getKpiFormula("avg-days-collect", periodLong)} valueClass={avgDaysClass}
                    periodTag={periodLong} isLoading={businessLoading}
                    onClick={() => openKpiDetail("avg-days-collect")} />
                </div>
              </div>

              {/* ── Cash Health ───────────────────────────────────────── */}
              <div className={cashflowLoading ? "opacity-60 transition-opacity" : "transition-opacity"}>
                <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground/70 mb-3">
                  Bank-Based Metrics
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricCard label="Cash Balance"
                    value={formatAmount(cashOnHand)}
                    compactValue={formatAmountCompact(cashOnHand)}
                    subtitle="Connected bank accounts · excl. cards" formula={getKpiFormula("cash-on-hand", periodLong)}
                    valueClass="text-emerald-600" liveTag isLoading={cashflowLoading} bankOnly
                    onClick={() => openKpiDetail("cash-on-hand")} />
                  <MetricCard label="Net Cash Flow"
                    value={hasBankData ? formatAmount(netCashFlow) : "—"}
                    compactValue={hasBankData ? formatAmountCompact(netCashFlow) : undefined}
                    subtitle={hasBankData ? "All bank inflows − outflows" : "Bank data required"}
                    formula={getKpiFormula("net-cash-flow", periodLong)}
                    valueClass={!hasBankData ? "text-muted-foreground" : netCashFlow >= 0 ? "text-emerald-600" : "text-red-600"}
                    trend={hasBankData && netCashFlow !== 0 ? (netCashFlow > 0 ? "up" : "down") : undefined}
                    liveTag isLoading={cashflowLoading} bankOnly
                    onClick={() => openKpiDetail("net-cash-flow")} />
                  <MetricCard label="Monthly Burn Rate"
                    value={burnLabel} subtitle={burnSubtitle} formula={getKpiFormula("burn-rate", periodLong, periodMonths)}
                    valueClass={burnRate > 0 ? "text-rose-600" : hasBankData ? "text-emerald-600" : "text-muted-foreground"}
                    periodTag={periodLong} isLoading={cashflowLoading} bankOnly
                    onClick={() => openKpiDetail("burn-rate")} />
                  <MetricCard label="Runway"
                    value={runwayLabel} subtitle={runwaySubtitle} formula={getKpiFormula("runway", periodLong)}
                    valueClass={runwayClass} liveTag isLoading={cashflowLoading} bankOnly
                    onClick={() => openKpiDetail("runway")} />
                  <div className="col-span-2">
                    <MetricCard label="Non-Operating Cash Flow"
                      value={hasBankData ? formatAmount(nonOpCashFlow) : "—"}
                      compactValue={hasBankData ? formatAmountCompact(nonOpCashFlow) : undefined}
                      subtitle="Net Cash Flow − Operating Cash Flow"
                      formula="Loans, investments, and transfers not captured in operations"
                      valueClass={!hasBankData ? "text-muted-foreground" : nonOpCashFlow >= 0 ? "text-sky-600" : "text-amber-600"}
                      trend={hasBankData && nonOpCashFlow !== 0 ? (nonOpCashFlow > 0 ? "up" : "down") : undefined}
                      periodTag={periodLong} isLoading={cashflowLoading} bankOnly />
                  </div>
                </div>
              </div>
            </CardContent>
          </div>

          <KpiDetailSheet
            open={!!kpiDetailKey}
            onClose={() => setKpiDetailKey(null)}
            kpiKey={kpiDetailKey}
            invoices={detailInvoices}
            collectedInvoices={detailCollectedInvoices}
            cashInflows={detailCashInflows}
            cashOutflows={detailCashOutflows}
            bankAccounts={detailBankAccounts}
            expenses={detailExpenses}
            loading={detailLoading}

            receivables={receivables}
            agedAR={agedAR}
            endDate={resolvedRange.endDate}
            accounts={accounts}
            period={periodLong}
            periodMonths={periodMonths}
            dateFilterKey={dateFilterKey}
            onNavigate={onNavigate}
            numerics={numericsForDetail}
          />
        </Card>
      )}

      {/* ── For You ──────────────────────────────────────────────────────────── */}
      {view === "for-you" && (
        <Card className="m-6 shadow-md">
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <CardTitle>For You</CardTitle>
                {criticalOrHighCount > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200">
                    <XCircle className="h-3 w-3" />
                    {criticalOrHighCount} critical
                  </span>
                )}
              </div>
              <button onClick={() => fetchAll(true)} disabled={signalsLoading}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50">
                <RefreshCw className={`h-3.5 w-3.5 ${signalsLoading ? "animate-spin" : ""}`} /> Refresh
              </button>
            </div>
          </CardHeader>

          <CardContent className="p-6 space-y-6">
            {signalsLoading ? (
              <div className="flex flex-col items-center gap-3 py-12 text-muted-foreground">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-sm">Loading...</span>
              </div>
            ) : (
              <>
                {/* Requires Attention */}
                {((receivables && (receivables.atRisk.count + receivables.stuck.count) > 0) || financialSignals.some((s) => s.level === "critical" || s.level === "high")) && (
                  <div>
                    <h2 className="text-xs font-bold uppercase tracking-widest text-red-600/80 mb-3">
                      Requires Attention
                    </h2>
                    <div className="space-y-2">
                      {receivables && receivables.atRisk.count > 0 && (
                        <button onClick={() => openKpiDetail("at-risk")}
                          className="w-full flex items-center gap-3 p-4 rounded-lg border bg-red-50 border-red-200 text-left hover:bg-red-100 transition-colors group">
                          <XCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-red-700">
                              {formatAmount(receivables.atRisk.amount)} at risk — {receivables.atRisk.count} invoice{receivables.atRisk.count !== 1 ? "s" : ""} · 1–30 days overdue
                            </p>
                          </div>
                          <ArrowRight className="h-4 w-4 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                        </button>
                      )}
                      {receivables && receivables.stuck.count > 0 && (
                        <button onClick={() => openKpiDetail("stuck-30")}
                          className="w-full flex items-center gap-3 p-4 rounded-lg border bg-amber-50 border-amber-200 text-left hover:bg-amber-100 transition-colors group">
                          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-amber-700">
                              {formatAmount(receivables.stuck.amount)} overdue — {receivables.stuck.count} invoice{receivables.stuck.count !== 1 ? "s" : ""} · more than 30 days past due
                            </p>
                          </div>
                          <ArrowRight className="h-4 w-4 text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                        </button>
                      )}
                      {financialSignals
                        .filter((s) => s.level === "critical" || s.level === "high")
                        .map((s) => <SignalCard key={s.id} signal={s} />)}
                    </div>
                  </div>
                )}

                {/* Financial Alerts */}
                {financialSignals.filter((s) => s.level === "warning" || s.level === "info").length > 0 && (
                  <div>
                    <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground/70 mb-3">
                      Financial Alerts
                    </h2>
                    <div className="space-y-2">
                      {financialSignals
                        .filter((s) => s.level === "warning" || s.level === "info")
                        .map((s) => <SignalCard key={s.id} signal={s} />)}
                    </div>
                  </div>
                )}

                {/* All clear */}
                {financialSignals.length === 0 && (!receivables || (receivables.atRisk.count + receivables.stuck.count) === 0) && (
                  <div className="flex items-center gap-2.5 px-4 py-3 rounded-lg border bg-emerald-50 border-emerald-200">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                    <p className="text-sm font-medium text-emerald-700">
                      No issues detected — all financial signals are within normal range
                    </p>
                  </div>
                )}

                {/* System Health */}
                <div>
                  <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground/70 mb-3 flex items-center gap-2">
                    <Activity className="h-3.5 w-3.5" />
                    System Health
                  </h2>
                  {qbSyncSignal && (
                    <div className="mb-3"><SignalCard signal={qbSyncSignal} /></div>
                  )}
                  <div className="flex flex-wrap gap-3">
                    {(() => {
                      const isAlert = !!qbSyncSignal;
                      const isStale = !isAlert && qbSyncStale;
                      const dotColor = isAlert || isStale ? "bg-amber-400" : "bg-emerald-400";
                      const borderCls = isAlert || isStale ? "border-amber-300" : "";
                      const timeText = qbSyncedAt
                        ? `synced ${formatDistanceToNow(qbSyncedAt, { addSuffix: true })}`
                        : refreshedAt
                          ? `data loaded ${formatDistanceToNow(refreshedAt, { addSuffix: true })}`
                          : "connected";
                      return (
                        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border bg-card text-sm ${borderCls}`}>
                          <div className={`w-2 h-2 rounded-full ${dotColor}`} />
                          <span className="font-medium text-foreground">QuickBooks</span>
                          <span className={`text-xs ${isAlert || isStale ? "text-amber-600 font-medium" : "text-muted-foreground"}`}>
                            {timeText}
                          </span>
                          {(isAlert || isStale) && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </>
            )}
          </CardContent>

          <KpiDetailSheet
            open={!!kpiDetailKey}
            onClose={() => setKpiDetailKey(null)}
            kpiKey={kpiDetailKey}
            invoices={detailInvoices}
            collectedInvoices={detailCollectedInvoices}
            cashInflows={detailCashInflows}
            cashOutflows={detailCashOutflows}
            bankAccounts={detailBankAccounts}
            expenses={detailExpenses}
            loading={detailLoading}

            receivables={receivables}
            agedAR={agedAR}
            endDate={resolvedRange.endDate}
            accounts={accounts}
            period={periodLong}
            periodMonths={periodMonths}
            dateFilterKey={dateFilterKey}
            onNavigate={onNavigate}
            numerics={numericsForDetail}
          />
        </Card>
      )}

    </TooltipProvider>
  );
};

export default FinancialControlPanel;
